import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const FleetAnimationControls = ({ 
  cesiumViewer, 
  mapData = [], 
  onDebugUpdate,
  loading = false,
  animationTrigger = 0,
  onCancelAnimation,
  scallopFleetActive = false
}) => {
  // Fleet animation state
  const [showTripRoutes, setShowTripRoutes] = useState(false); // Don't play by default
  
  // Refs for managing data sources
  const fleetEntitiesRef = useRef([]);
  const scallopFleetEntitiesRef = useRef([]);

  // SAMS access configuration
  const samsAccessConfig = useMemo(() => ({
    'DMV': { mode: 'das_based' },
    'HCS': { mode: 'das_based' },
    'NYB': { mode: 'das_based' },
    'NLS-West': { mode: 'das_based' },
    'NLS-Deep': { mode: 'das_based' },
    'NLS-Central': { mode: 'das_based' },
    'NLS-South': { mode: 'das_based' },
    'CA1': { mode: 'trip_based', trips_per_vessel: 1, possession_limit_lbs: 12000 },
    'CA2-N': { mode: 'trip_based', trips_per_vessel: 1, possession_limit_lbs: 12000 },
    'CA2-S': { mode: 'closed' },
    'ET-Flex': { mode: 'das_based' },
    'ET-Open': { mode: 'das_based' },
    'HC-Inshore': { mode: 'das_based' },
    'SCB': { mode: 'das_based' },
    'Elephant-Trunk': { mode: 'das_based' },
    'Nantucket-Lightship': { mode: 'das_based' }
  }), []);

  // New Bedford port coordinates
  const newBedfordPort = useMemo(() => ({
    name: 'New Bedford',
    latitude: 41.6362,
    longitude: -70.9342
  }), []);

  // SAMS area coordinates (approximate centroids for das_based areas)
  const samsCoordinates = useMemo(() => ({
    'DMV': { latitude: 37.2, longitude: -75.3, name: 'DMV' },
    'HCS': { latitude: 38.7, longitude: -73.7, name: 'HCS' },
    'NYB': { latitude: 39.1, longitude: -73.8, name: 'NYB' },
    'NLS-West': { latitude: 40.3, longitude: -69.0, name: 'NLS-West' },
    'NLS-Deep': { latitude: 40.5, longitude: -68.8, name: 'NLS-Deep' },
    'NLS-Central': { latitude: 40.6, longitude: -68.5, name: 'NLS-Central' },
    'NLS-South': { latitude: 40.4, longitude: -68.6, name: 'NLS-South' },
    'ET-Flex': { latitude: 38.1, longitude: -74.8, name: 'ET-Flex' },
    'ET-Open': { latitude: 38.2, longitude: -74.6, name: 'ET-Open' },
    'HC-Inshore': { latitude: 40.1, longitude: -72.5, name: 'HC-Inshore' },
    'SCB': { latitude: 40.8, longitude: -68.2, name: 'SCB' },
    'Elephant-Trunk': { latitude: 38.5, longitude: -74.2, name: 'Elephant-Trunk' },
    'Nantucket-Lightship': { latitude: 40.2, longitude: -69.1, name: 'Nantucket-Lightship' }
  }), []);

  const logDebug = useCallback((message, data = null) => {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    console.log(`[FLEET ${timestamp}] ${message}`, data || '');
    if (onDebugUpdate) {
      onDebugUpdate(`[${timestamp}] ${message}`, data);
    }
  }, [onDebugUpdate]);

  // Generate mock scallop fishing data
  const generateScallopFleetData = useCallback(() => {
    const dasBasedAreas = Object.entries(samsAccessConfig)
      .filter(([area, config]) => config.mode === 'das_based')
      .map(([area]) => area);

    const mockVessels = [];
    const vesselNames = [
      'Atlantic Star', 'Sea Harvest', 'Ocean Pride', 'Deep Sea', 'Maritime',
      'Coastal Queen', 'Fishing Glory', 'Sea Breeze', 'Ocean Explorer', 'Marine Master',
      'Atlantic Pearl', 'Sea Fortune', 'Ocean Gem', 'Deep Blue', 'Maritime Star',
      'Coastal King', 'Fishing Legend', 'Sea Spirit', 'Ocean Warrior', 'Marine Champion'
    ];

    // Generate 20 vessels with multiple trips over several days
    for (let vesselIndex = 0; vesselIndex < 20; vesselIndex++) {
      const vesselName = vesselNames[vesselIndex];
      const vesselId = `SCALLOP-${String(vesselIndex + 1).padStart(3, '0')}`;
      
      // Each vessel makes 3-5 trips over 5 days
      const numTrips = 3 + Math.floor(Math.random() * 3); // 3-5 trips
      
      for (let tripIndex = 0; tripIndex < numTrips; tripIndex++) {
        const targetArea = dasBasedAreas[Math.floor(Math.random() * dasBasedAreas.length)];
        const areaCoords = samsCoordinates[targetArea];
        
        if (areaCoords) {
          mockVessels.push({
            trip_number: tripIndex + 1,
            vessel_id: vesselId,
            vessel_name: vesselName,
            port_name: newBedfordPort.name,
            port_latitude: newBedfordPort.latitude,
            port_longitude: newBedfordPort.longitude,
            target_area: targetArea,
            target_area_latitude: areaCoords.latitude,
            target_area_longitude: areaCoords.longitude,
            fishing_type: 'scallop',
            day: Math.floor(tripIndex / 2) + 1, // Spread trips across days
            departure_time: `2024-01-${String(Math.floor(tripIndex / 2) + 1).padStart(2, '0')}T${String(6 + (tripIndex % 2) * 12).padStart(2, '0')}:00:00Z`
          });
        }
      }
    }

    return mockVessels;
  }, [samsAccessConfig, samsCoordinates, newBedfordPort]);

  // Track previous trigger value to detect changes
  const prevTriggerRef = useRef(0);
  const hasInitializedRef = useRef(false);
  
  // Respond to animation trigger from play button (for regular fleet only)
  useEffect(() => {
    console.log('🔄 FleetAnimationControls received animationTrigger:', animationTrigger, 'prev:', prevTriggerRef.current, 'hasInitialized:', hasInitializedRef.current);
    
    // On mount, if we receive a non-zero trigger, initialize state based on trigger value
    // This handles the case where the component mounts after the trigger was already set
    if (!hasInitializedRef.current && animationTrigger > 0) {
      console.log('🎬 Component mounted with existing trigger - initializing animation state');
      hasInitializedRef.current = true;
      const isPlayAction = animationTrigger % 2 === 1; // odd numbers = play (1, 3, 5...)
      
      if (isPlayAction) {
        console.log('🎬 Setting showTripRoutes to true (regular fleet) - component mounted with play trigger');
        setShowTripRoutes(true);
        logDebug('PLAY: Regular fleet animation triggered on mount');
      } else {
        console.log('⏹ Setting showTripRoutes to false (regular fleet) - component mounted with cancel trigger');
        setShowTripRoutes(false);
        // Also stop the clock to ensure animation is stopped
        if (cesiumViewer && cesiumViewer.clock) {
          cesiumViewer.clock.shouldAnimate = false;
        }
        logDebug('STOP: Regular fleet animation stopped on mount');
      }
      
      prevTriggerRef.current = animationTrigger;
      return;
    }
    
    // Normal trigger change handling
    if (animationTrigger > 0 && animationTrigger !== prevTriggerRef.current) {
      hasInitializedRef.current = true;
      // Use a simple approach: even triggers = play, odd triggers = cancel
      const isPlayAction = animationTrigger % 2 === 1; // odd numbers = play (1, 3, 5...)
      
      if (isPlayAction) {
        console.log('🎬 Setting showTripRoutes to true (regular fleet)');
        setShowTripRoutes(true);
        logDebug('PLAY: Regular fleet animation triggered by play button');
      } else {
        console.log('⏹ Setting showTripRoutes to false (regular fleet) - clearing animation');
        setShowTripRoutes(false);
        // Stop the clock immediately
        if (cesiumViewer && cesiumViewer.clock) {
          cesiumViewer.clock.shouldAnimate = false;
          console.log('⏹ Stopped Cesium clock (cancel button pressed)');
        }
        // Clear entities immediately - search viewer directly to find all fleet entities
        if (cesiumViewer && !cesiumViewer.isDestroyed()) {
          // First, clear entities from our ref
          fleetEntitiesRef.current.forEach(entity => {
            try {
              if (cesiumViewer.entities.contains(entity)) {
                cesiumViewer.entities.remove(entity);
              }
            } catch (error) {
              console.warn('⚠️ Error removing entity from ref:', error);
            }
          });
          fleetEntitiesRef.current = [];
          
          // Also search viewer for any fleet entities that might be in other instances
          const entitiesToRemove = [];
          cesiumViewer.entities.values.forEach(entity => {
            const props = entity.properties;
            if (props) {
              const dataType = props.getValue ? props.getValue().dataType : props.dataType;
              const entityId = entity.id || '';
              // Check if this is a fleet entity (vessel entity)
              if (dataType === 'vessel' || entityId.includes('vessel-')) {
                entitiesToRemove.push(entity);
              }
            }
          });
          
          // Remove all found fleet entities
          entitiesToRemove.forEach(entity => {
            try {
              cesiumViewer.entities.remove(entity);
            } catch (error) {
              console.warn('⚠️ Error removing fleet entity:', error);
            }
          });
          
          console.log(`🧹 Cleared ${entitiesToRemove.length} fleet entities from viewer (cancel button pressed)`);
        }
        logDebug('STOP: Regular fleet animation stopped and cleared by cancel button');
      }
      
      prevTriggerRef.current = animationTrigger;
    }
  }, [animationTrigger, logDebug]);


  // Handle fleet data visualization - ONLY when showTripRoutes changes or mapData changes
  useEffect(() => {
    if (!cesiumViewer || !window.Cesium || !mapData || mapData.length === 0) {
      return;
    }

    const updateFleetVisualization = () => {
      try {
        console.log('🎬 FleetAnimationControls: Starting fleet visualization', { 
          dataCount: mapData.length,
          showTripRoutes,
          scallopFleetActive
        });
        logDebug('UPDATING: Fleet data visualization', { 
          dataCount: mapData.length,
          showTripRoutes
        });
        
        // Clear existing fleet entities from ref
        fleetEntitiesRef.current.forEach(entity => {
          try {
            if (cesiumViewer.entities.contains(entity)) {
              cesiumViewer.entities.remove(entity);
            }
          } catch (error) {
            console.warn('⚠️ Error removing entity from ref:', error);
          }
        });
        fleetEntitiesRef.current = [];

        // Stop the clock and clear groundfish entities only when groundfish is not active
        if (!showTripRoutes) {
          if (cesiumViewer.clock) {
            cesiumViewer.clock.shouldAnimate = false;
            console.log('⏹ Stopped Cesium clock (regular fleet animation stopped)');
          }
          
          // Search and remove ALL fleet entities from viewer (in case they're from other instances)
          const entitiesToRemove = [];
          cesiumViewer.entities.values.forEach(entity => {
            const props = entity.properties;
            if (props) {
              const dataType = props.getValue ? props.getValue().dataType : props.dataType;
              const entityId = entity.id || '';
              // Check if this is a fleet entity (vessel entity) - but not scallop
              if (dataType === 'vessel' && !entityId.includes('scallop')) {
                const label = entity.label;
                const labelText = label && label.text ? (label.text.getValue ? label.text.getValue() : label.text) : '';
                // Make sure it's not a scallop vessel
                if (!labelText || !labelText.includes('Scallop')) {
                  entitiesToRemove.push(entity);
                }
              } else if (entityId.includes('vessel-') && !entityId.includes('scallop')) {
                entitiesToRemove.push(entity);
              }
            }
          });
          
          // Remove all found fleet entities
          entitiesToRemove.forEach(entity => {
            try {
              cesiumViewer.entities.remove(entity);
            } catch (error) {
              console.warn('⚠️ Error removing fleet entity:', error);
            }
          });
          
          if (entitiesToRemove.length > 0) {
            console.log(`🧹 Cleared ${entitiesToRemove.length} fleet entities from viewer (showTripRoutes = false)`);
          }
        }

        // Add vessel routes and animations
        if (showTripRoutes) {
          const legDuration = 30;   // seconds for each travel leg
          const pauseDuration = 3;  // pause at fishing area
          const waveDuration = legDuration * 2 + pauseDuration;
          const maxStagger = 15;    // max random delay (seconds) within a wave
        
          const startTime = window.Cesium.JulianDate.now();
          const maxTripNumber = Math.max(...mapData.map((t) => t.trip_number));
          const totalDuration = maxTripNumber * waveDuration;
        
          cesiumViewer.clock.startTime = startTime.clone();
          cesiumViewer.clock.stopTime = window.Cesium.JulianDate.addSeconds(
            startTime,
            totalDuration,
            new window.Cesium.JulianDate()
          );
          cesiumViewer.clock.currentTime = startTime.clone();
          cesiumViewer.clock.shouldAnimate = true;
          cesiumViewer.clock.multiplier = 15.0; // Speed up animation
          if (cesiumViewer.scene) cesiumViewer.scene.requestRender();

          // Group trips by trip_number for wave-based animation
          const tripsByNumber = {};
          mapData.forEach(trip => {
            if (!tripsByNumber[trip.trip_number]) {
              tripsByNumber[trip.trip_number] = [];
            }
            tripsByNumber[trip.trip_number].push(trip);
          });

          console.log('📊 FleetAnimationControls: Processing trips', {
            totalTrips: mapData.length,
            uniqueTripNumbers: Object.keys(tripsByNumber).length,
            tripsByNumber: Object.keys(tripsByNumber).map(num => ({ tripNumber: num, count: tripsByNumber[num].length }))
          });

          let processedTrips = 0;
          let skippedTrips = 0;

          // Create animated vessels for each trip
          Object.entries(tripsByNumber).forEach(([tripNumber, trips]) => {
            trips.forEach((trip, index) => {
              // Validate trip data has required coordinates
              if (!trip.port_longitude || !trip.port_latitude || !trip.target_area_longitude || !trip.target_area_latitude) {
                console.warn('⚠️ Skipping trip with missing coordinates:', trip);
                skippedTrips++;
                return;
              }

              processedTrips++;

              const staggerDelay = Math.random() * maxStagger;
              const tripStartTime = window.Cesium.JulianDate.addSeconds(
                startTime,
                (parseInt(tripNumber) - 1) * waveDuration + staggerDelay,
                new window.Cesium.JulianDate()
              );

              // Create vessel path
              const positions = [];
              const times = [];
              let currentTime = tripStartTime.clone();

              // Port to fishing area
              positions.push(window.Cesium.Cartesian3.fromDegrees(trip.port_longitude, trip.port_latitude));
              times.push(window.Cesium.JulianDate.addSeconds(currentTime, 0, new window.Cesium.JulianDate()));
              
              positions.push(window.Cesium.Cartesian3.fromDegrees(trip.target_area_longitude, trip.target_area_latitude));
              times.push(window.Cesium.JulianDate.addSeconds(currentTime, legDuration, new window.Cesium.JulianDate()));
              
              // Pause at fishing area
              currentTime = window.Cesium.JulianDate.addSeconds(currentTime, legDuration + pauseDuration, new window.Cesium.JulianDate());
              
              // Fishing area back to port
              positions.push(window.Cesium.Cartesian3.fromDegrees(trip.target_area_longitude, trip.target_area_latitude));
              times.push(window.Cesium.JulianDate.addSeconds(currentTime, 0, new window.Cesium.JulianDate()));
              
              positions.push(window.Cesium.Cartesian3.fromDegrees(trip.port_longitude, trip.port_latitude));
              times.push(window.Cesium.JulianDate.addSeconds(currentTime, legDuration, new window.Cesium.JulianDate()));

              // Create the vessel entity with animation
              const vesselEntity = cesiumViewer.entities.add({
                id: `vessel-${trip.trip_number}-${index}`,
                position: positions[0],
                path: {
                  leadTime: 0,
                  trailTime: legDuration * 2,
                  width: 0, // change to make path visible
                  material: window.Cesium.Color.YELLOW,
                  resolution: 1,
                  distanceDisplayCondition: new window.Cesium.DistanceDisplayCondition(0.0, 1000000.0)
                },
                properties: {
                  tripNumber: trip.trip_number,
                  vesselId: trip.vessel_id,
                  portName: trip.port_name,
                  dataType: 'vessel'
                }
              });

              // Set up the position property with time-based animation
              vesselEntity.position = new window.Cesium.SampledPositionProperty();
              for (let i = 0; i < positions.length; i++) {
                vesselEntity.position.addSample(times[i], positions[i]);
              }

              // Add vessel marker at current position
              vesselEntity.point = {
                pixelSize: 8,
                color: window.Cesium.Color.YELLOW,
                outlineColor: window.Cesium.Color.BLACK,
                outlineWidth: 2,
                heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND
              };

              // Add vessel label
              vesselEntity.label = {
                text: trip.vessel_name || "Vessel",
                font: "8pt sans-serif",
                style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
                fillColor: window.Cesium.Color.YELLOW,
                outlineColor: window.Cesium.Color.BLACK,
                outlineWidth: 2,
                pixelOffset: new window.Cesium.Cartesian2(0, -20),
                showBackground: false,
              };

              fleetEntitiesRef.current.push(vesselEntity);
            });
          });

          console.log('✅ FleetAnimationControls: Animation setup complete', {
            processedTrips,
            skippedTrips,
            totalEntities: fleetEntitiesRef.current.length
          });

          logDebug('SUCCESS: Fleet data updated', {
            routes: showTripRoutes ? mapData.length : 0,
            processedTrips,
            skippedTrips
          });
        }
      } catch (error) {
        logDebug('ERROR: Fleet data update failed', { error: error.message });
      }
    };

    updateFleetVisualization();
  }, [mapData, showTripRoutes, scallopFleetActive, animationTrigger, cesiumViewer, logDebug]);

  // Immediately clear scallop fleet entities when it's canceled (before effect runs)
  useEffect(() => {
    if (!scallopFleetActive && scallopFleetEntitiesRef.current.length > 0) {
      console.log('⏹ Scallop fleet canceled - clearing entities immediately');
      if (cesiumViewer && !cesiumViewer.isDestroyed()) {
        // First, clear entities from our ref
        scallopFleetEntitiesRef.current.forEach(entity => {
          try {
            if (cesiumViewer.entities.contains(entity)) {
              cesiumViewer.entities.remove(entity);
            }
          } catch (error) {
            console.warn('⚠️ Error removing scallop entity from ref:', error);
          }
        });
        scallopFleetEntitiesRef.current = [];
        
        // Also search viewer for any scallop fleet entities that might be in other instances
        const entitiesToRemove = [];
        cesiumViewer.entities.values.forEach(entity => {
          const props = entity.properties;
          if (props) {
            const dataType = props.getValue ? props.getValue().dataType : props.dataType;
            const entityId = entity.id || '';
            const label = entity.label;
            // Check if this is a scallop fleet entity
            if (dataType === 'vessel' && label && label.text && label.text.getValue && label.text.getValue().includes('Scallop')) {
              entitiesToRemove.push(entity);
            } else if (entityId.includes('scallop') || (label && label.text && typeof label.text === 'string' && label.text.includes('Scallop'))) {
              entitiesToRemove.push(entity);
            }
          }
        });
        
        // Remove all found scallop fleet entities
        entitiesToRemove.forEach(entity => {
          try {
            cesiumViewer.entities.remove(entity);
          } catch (error) {
            console.warn('⚠️ Error removing scallop fleet entity:', error);
          }
        });
        
        console.log(`🧹 Cleared ${entitiesToRemove.length} scallop fleet entities from viewer (cancel button pressed)`);
        
        // Stop clock if regular fleet is also not active
        if (!showTripRoutes && cesiumViewer.clock) {
          cesiumViewer.clock.shouldAnimate = false;
          console.log('⏹ Stopped Cesium clock (scallop fleet canceled)');
        }
      }
    }
  }, [scallopFleetActive, cesiumViewer, showTripRoutes]);

  // Handle scallop fleet visualization
  useEffect(() => {
    if (!cesiumViewer || !window.Cesium) {
      return;
    }

    const updateScallopFleetVisualization = () => {
      try {
        console.log('🦪 FleetAnimationControls: Starting scallop fleet visualization', { 
          scallopFleetActive
        });
        logDebug('UPDATING: Scallop fleet visualization', { 
          scallopFleetActive
        });
        
        // Clear existing scallop fleet entities
        scallopFleetEntitiesRef.current.forEach(entity => {
          cesiumViewer.entities.remove(entity);
        });
        scallopFleetEntitiesRef.current = [];

        // Stop the clock only when neither animation is active
        if (!scallopFleetActive && !showTripRoutes) {
          if (cesiumViewer.clock) {
            cesiumViewer.clock.shouldAnimate = false;
            console.log('⏹ Stopped Cesium clock (no fleet animations active)');
          }
        }

        // Add scallop vessel routes and animations
        if (scallopFleetActive) {
          const scallopData = generateScallopFleetData();
          console.log('🦪 Generated scallop fleet data:', scallopData.length, 'trips');
          
          const legDuration = 25;   // seconds for each travel leg
          const pauseDuration = 4;  // pause at fishing area
          const waveDuration = legDuration * 2 + pauseDuration;
          const maxStagger = 12;    // max random delay (seconds) within a wave
        
          const startTime = window.Cesium.JulianDate.now();
          const maxTripNumber = Math.max(...scallopData.map((t) => t.trip_number));
          const totalDuration = maxTripNumber * waveDuration;
        
          cesiumViewer.clock.startTime = startTime.clone();
          cesiumViewer.clock.stopTime = window.Cesium.JulianDate.addSeconds(
            startTime,
            totalDuration,
            new window.Cesium.JulianDate()
          );
          cesiumViewer.clock.currentTime = startTime.clone();
          cesiumViewer.clock.shouldAnimate = true;
          cesiumViewer.clock.multiplier = 12.0; // Speed up animation
          if (cesiumViewer.scene) cesiumViewer.scene.requestRender();

          // Group trips by trip_number for wave-based animation
          const tripsByNumber = {};
          scallopData.forEach(trip => {
            if (!tripsByNumber[trip.trip_number]) {
              tripsByNumber[trip.trip_number] = [];
            }
            tripsByNumber[trip.trip_number].push(trip);
          });

          console.log('🦪 FleetAnimationControls: Processing scallop trips', {
            totalTrips: scallopData.length,
            uniqueTripNumbers: Object.keys(tripsByNumber).length,
            tripsByNumber: Object.keys(tripsByNumber).map(num => ({ tripNumber: num, count: tripsByNumber[num].length }))
          });

          let processedTrips = 0;
          let skippedTrips = 0;

          // Create animated vessels for each trip
          Object.entries(tripsByNumber).forEach(([tripNumber, trips]) => {
            trips.forEach((trip, index) => {
              // Validate trip data has required coordinates
              if (!trip.port_longitude || !trip.port_latitude || !trip.target_area_longitude || !trip.target_area_latitude) {
                console.warn('⚠️ Skipping scallop trip with missing coordinates:', trip);
                skippedTrips++;
                return;
              }

              processedTrips++;

              const staggerDelay = Math.random() * maxStagger;
              const tripStartTime = window.Cesium.JulianDate.addSeconds(
                startTime,
                (parseInt(tripNumber) - 1) * waveDuration + staggerDelay,
                new window.Cesium.JulianDate()
              );

              // Create vessel path
              const positions = [];
              const times = [];
              let currentTime = tripStartTime.clone();

              // Port to fishing area
              positions.push(window.Cesium.Cartesian3.fromDegrees(trip.port_longitude, trip.port_latitude));
              times.push(window.Cesium.JulianDate.addSeconds(currentTime, 0, new window.Cesium.JulianDate()));
              
              positions.push(window.Cesium.Cartesian3.fromDegrees(trip.target_area_longitude, trip.target_area_latitude));
              times.push(window.Cesium.JulianDate.addSeconds(currentTime, legDuration, new window.Cesium.JulianDate()));
              
              // Pause at fishing area
              currentTime = window.Cesium.JulianDate.addSeconds(currentTime, legDuration + pauseDuration, new window.Cesium.JulianDate());
              
              // Fishing area back to port
              positions.push(window.Cesium.Cartesian3.fromDegrees(trip.target_area_longitude, trip.target_area_latitude));
              times.push(window.Cesium.JulianDate.addSeconds(currentTime, 0, new window.Cesium.JulianDate()));
              
              positions.push(window.Cesium.Cartesian3.fromDegrees(trip.port_longitude, trip.port_latitude));
              times.push(window.Cesium.JulianDate.addSeconds(currentTime, legDuration, new window.Cesium.JulianDate()));

              // Create the vessel entity with animation
              const vesselEntity = cesiumViewer.entities.add({
                id: `scallop-vessel-${trip.trip_number}-${index}`,
                position: positions[0],
                path: {
                  leadTime: 0,
                  trailTime: legDuration * 2,
                  width: 0, // change to make path visible
                  material: window.Cesium.Color.YELLOW,
                  resolution: 1,
                  distanceDisplayCondition: new window.Cesium.DistanceDisplayCondition(0.0, 1000000.0)
                },
                properties: {
                  tripNumber: trip.trip_number,
                  vesselId: trip.vessel_id,
                  portName: trip.port_name,
                  targetArea: trip.target_area,
                  fishingType: 'scallop',
                  dataType: 'scallop-vessel'
                }
              });

              // Set up the position property with time-based animation
              vesselEntity.position = new window.Cesium.SampledPositionProperty();
              for (let i = 0; i < positions.length; i++) {
                vesselEntity.position.addSample(times[i], positions[i]);
              }

              // Add vessel marker at current position
              vesselEntity.point = {
                pixelSize: 10,
                color: window.Cesium.Color.YELLOW,
                outlineColor: window.Cesium.Color.BLACK,
                outlineWidth: 2,
                heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND
              };

              // Add vessel label
              vesselEntity.label = {
                text: trip.vessel_name || "Scallop Vessel",
                font: "8pt sans-serif",
                style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
                fillColor: window.Cesium.Color.YELLOW,
                outlineColor: window.Cesium.Color.BLACK,
                outlineWidth: 2,
                pixelOffset: new window.Cesium.Cartesian2(0, -20),
                showBackground: false,
              };

              scallopFleetEntitiesRef.current.push(vesselEntity);
            });
          });

          console.log('✅ FleetAnimationControls: Scallop animation setup complete', {
            processedTrips,
            skippedTrips,
            totalEntities: scallopFleetEntitiesRef.current.length
          });

          logDebug('SUCCESS: Scallop fleet data updated', {
            routes: scallopFleetActive ? scallopData.length : 0,
            processedTrips,
            skippedTrips
          });
        }
      } catch (error) {
        logDebug('ERROR: Scallop fleet data update failed', { error: error.message });
      }
    };

    updateScallopFleetVisualization();
  }, [scallopFleetActive, showTripRoutes, cesiumViewer, logDebug, generateScallopFleetData]);

  // Note: We DON'T stop the clock on unmount - the animation should continue
  // even when the tab is closed. The hidden FleetAnimationControls instance
  // will continue managing the animation.

  if (!showTripRoutes && !scallopFleetActive) {
    return null;
  }

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      fontSize: '12px',
      color: '#4b5563',
      padding: '8px',
      backgroundColor: '#f9fafb',
      borderRadius: '4px',
      border: '1px solid #e5e7eb'
    }}>
      <span style={{ 
        display: 'inline-block', 
        width: '12px', 
        height: '12px', 
        backgroundColor: showTripRoutes ? '#10b981' : '#6b7280',
        borderRadius: '50%',
        marginRight: '8px',
        border: '2px solid white',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.2)'
      }}></span>
      <span style={{ fontWeight: '500' }}>
        {showTripRoutes ? 'Animation Active' : 'Animation Stopped'} - {mapData ? mapData.length : 0} Vessels
      </span>
    </div>
  );
};

export default FleetAnimationControls;
